import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'
import { cn } from '@/shared/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring-offset-surface border border-transparent',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground shadow-[0_18px_42px_-20px_rgba(255,46,166,0.75)] hover:bg-primary-soft',
        secondary:
          'bg-card text-slate-100 hover:bg-card/90',
        ghost: 'bg-transparent text-slate-200 hover:bg-white/10 border-none',
        outline:
          'border border-white/12 bg-transparent text-slate-200 hover:border-primary hover:text-primary-soft',
        subtle: 'bg-card-muted text-slate-200 hover:bg-card',
      },
      size: {
        sm: 'h-9 px-4 text-xs',
        md: 'h-11 px-6 text-sm',
        lg: 'h-[50px] px-8 text-base',
        icon: 'h-11 w-11 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'
    return <Component ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  },
)

Button.displayName = 'Button'
